import * as _ from 'lodash';
import * as utils from '../../utils';
import { cropImage, getImageDimensions, stitchImages } from '../../utils/imageUtils';
import { getLogger } from '../../commons/logger';
import type { WindowUtils } from './windowUtils';
import type { ScreenshotUtils } from './screenshotUtils';
import type { VisibilityInfo } from 'clickim/src/background/visibilityUtils';
import type { IImageCaptureUtils } from 'clickim/src/background/session/ITabService';

const logger = getLogger('image-capture-utils');
class RectIsOutsideOfImageError extends Error {
    public readonly rectIsOutsideOfImageError = true;
}

interface ImagePart {
    position: {
        left: number;
        top: number;
    };
    size: {
        width: number;
        height: number;
    };
    image: string;
}
interface ImageData { elementRect?: Omit<VisibilityInfo, 'bottom' | 'right'>; offset?: { top: number; left: number } }
async function cropImageFromImageData(imageData: ImageData, imageInfo: utils.AwaitedReturnType<ScreenshotUtils['takeScreenshot']>) {
    const _image = (imageInfo.image || imageInfo) as string;
    const pixelRatio = imageInfo.devicePixelRatio;

    if (!_image) {
        throw new Error('Failed to get image');
    }

    const imageRegExMatch = _image.match(/^data:[^;]*;base64,(.*)$/);
    if (!imageRegExMatch) {
        throw new Error('Image is not in base64 format');
    }

    // chromeCropImage2
    const offset = imageData.offset || { top: 0, left: 0 };

    offset.top *= pixelRatio;
    offset.left *= pixelRatio;

    // elementImage
    if (!imageData.elementRect) {
        logger.warn('missing elementRect', _.omit(imageData, 'image'));
        return {};
    }

    const { elementRect } = imageData;
    let x = elementRect.left * pixelRatio + offset.left * pixelRatio;
    let y = elementRect.top * pixelRatio + offset.top * pixelRatio;
    let width = elementRect.width * pixelRatio;
    let height = elementRect.height * pixelRatio;

    if (x < 0) {
        width += x;
        width = Math.max(width, 0);
        x = 0;
    }

    if (y < 0) {
        height += y;
        height = Math.max(height, 0);
        y = 0;
    }

    const { width: imageWidth, height: imageHeight } = await getImageDimensions(imageRegExMatch[1]);
    if ((x + width) > imageWidth) {
        width = imageWidth - x;
    }

    if ((y + height) > imageHeight) {
        height = imageHeight - y;
    }

    if (height <= 0 || width <= 0) {
        throw new RectIsOutsideOfImageError('height or width is equal or lower than zero');
    }

    const base64 = await cropImage(imageRegExMatch[1], { left: x, top: y, width, height });
    return { elementImage: base64 };
}


function stitchImage(fullSize: utils.AwaitedReturnType<WindowUtils['getFullPageSize']>, parts: ImagePart[]) {
    return stitchImages(fullSize, parts);
}

// ???????????
async function uploadDataUrl() {
    return undefined;
}

async function uploadAllDataUrls(imagesObject: { screenImage: string; absoluteScreenHighlight: ReturnType<typeof getElementAbsoluteRectangle> }) {
    function isImageUrl(key: keyof typeof imagesObject) {
        const possibleImage = imagesObject[key];
        return typeof possibleImage === 'string' && possibleImage.startsWith('data');
    }

    async function updateKey(key: keyof typeof imagesObject) {
        // @ts-expect-error This is definitely an error, but this function seems unused
        const imageUrl = await uploadDataUrl(imagesObject[key]);
        return ({ key, url: imageUrl });
    }

    const keyUrlList = await utils.promiseMap((Object.keys(imagesObject) as (keyof typeof imagesObject)[]).filter(isImageUrl), updateKey);
    const entries = Object.fromEntries(keyUrlList.map(current => [current.key, current.url]));
    return Object.assign(imagesObject, entries);
}

function getElementAbsoluteRectangle(elementRect: ImageData['elementRect'], pixelRatio: number) {
    pixelRatio ||= 1;
    elementRect ||= {
        left: 0, top: 0, width: 0, height: 0,
    };
    return {
        left: pixelRatio * Math.round(elementRect.left),
        top: pixelRatio * Math.round(elementRect.top),
        width: pixelRatio * Math.round(elementRect.width),
        height: pixelRatio * Math.round(elementRect.height),
        pixelRatio,
    };
}

export class ImageCaptureUtils implements IImageCaptureUtils {
    constructor(_unusedId: unknown, private readonly windowUtils: WindowUtils, public readonly screenshotUtils: ScreenshotUtils) {
    }

    public async takeViewPortImage() {
        const imageInfo = await this.screenshotUtils.takeScreenshot();
        return typeof imageInfo === 'string' ? imageInfo : imageInfo.image;
    }

    public takeImageForComparison() {
        return this.takeViewPortImage();
    }

    public async takeAreaDataUrl(areas: ImageData) {
        const imageInfo = await this.screenshotUtils.takeScreenshot();
        const result = await cropImageFromImageData(areas, imageInfo);
        return Object.assign(result, {
            screenImage: imageInfo.image,
            absoluteScreenHighlight: getElementAbsoluteRectangle(areas.elementRect, imageInfo.devicePixelRatio),
        });
    }

    public async takeElementImage(elementId: string) {
        const imageInfo = await this.screenshotUtils.takeElementScreenshot({ ELEMENT: elementId });
        return {
            elementImage: typeof imageInfo === 'string' ? imageInfo : imageInfo.image,
        };
    }

    public async takeArea(areas: Pick<ImageData, 'elementRect'>) { // This seems unused
        const imageInfo = await this.screenshotUtils.takeScreenshot();
        return uploadAllDataUrls({
            screenImage: imageInfo.image,
            absoluteScreenHighlight: getElementAbsoluteRectangle(areas.elementRect, imageInfo.devicePixelRatio),
        });
    }

    public forcePixelRatio(forceRatio?: number) {
        return this.screenshotUtils.forcePixelRatio(forceRatio);
    }

    public getCurrentDevicePixelRatio() {
        return this.screenshotUtils.getCurrentDevicePixelRatio();
    }

    public async takeStitchedDataUrl(useImprovedScreenshotStitching?: boolean) {
        const { windowUtils, screenshotUtils } = this;

        const stabilize = () => utils.delay(250);
        const usingImprovedStitching = Boolean(useImprovedScreenshotStitching);

        async function createPart(position: ReturnType<typeof getPartsPositions>[number]['scrollPos'], crop: ReturnType<typeof getPartsPositions>[number]['cropData']) {
            if (usingImprovedStitching) {
                await windowUtils.scrollToPositionWithoutAnimation(position);
            } else {
                await windowUtils.scrollToPosition(position);
            }
            await stabilize();
            const imageInfo = await screenshotUtils.takeScreenshot();
            const cropResult = await cropImageFromImageData({ elementRect: crop }, imageInfo);
            return {
                position: { left: position.x + crop.left, top: position.y + crop.top },
                size: { width: crop.width, height: crop.height },
                image: cropResult.elementImage!,
            } as ImagePart;
        }

        async function takeAllParts(positionsData: ReturnType<typeof getPartsPositions>) {
            const allParts = [];
            for (const nextPos of positionsData) {
                const part = await createPart(nextPos.scrollPos, nextPos.cropData);
                allParts.push(part);
            }
            return allParts;
        }

        function getPartsPositions(fullPageSize: utils.AwaitedReturnType<WindowUtils['getFullPageSize']>, viewPortSize: utils.AwaitedReturnType<WindowUtils['getViewportSize']>) {
            const FPW = Math.max(fullPageSize.width, viewPortSize.width);
            const VPW = viewPortSize.width;
            const FPH = Math.max(fullPageSize.height, viewPortSize.height);
            const VPH = viewPortSize.height;
            const Ws = Array.from({ length: Math.ceil(FPW / VPW) }, (__, i) => ({
                scrollX: Math.min(i * VPW, FPW - VPW),
                cropX: i * VPW - Math.min(i * VPW, FPW - VPW),
                cropW: VPW - (i * VPW - Math.min(i * VPW, FPW - VPW)),
            }));
            const Hs = Array.from({ length: Math.ceil(FPH / VPH) }, (__, i) => ({
                scrollY: Math.min(i * VPH, FPH - VPH),
                cropY: i * VPH - Math.min(i * VPH, FPH - VPH),
                cropH: VPH - (i * VPH - Math.min(i * VPH, FPH - VPH)),
            }));
            return Ws.flatMap(w => Hs.map(h => ({
                scrollPos: { x: w.scrollX, y: h.scrollY },
                cropData: {
                    top: h.cropY,
                    left: w.cropX,
                    width: w.cropW,
                    height: h.cropH,
                },
            })));
        }

        async function createStitchImage(fullPageSize: utils.AwaitedReturnType<WindowUtils['getFullPageSize']>, viewPortSize: utils.AwaitedReturnType<WindowUtils['getViewportSize']>) {
            const originalPosition = await windowUtils.getCurrentScrollPosition();
            const positions = getPartsPositions(fullPageSize, viewPortSize);
            const parts = await takeAllParts(positions);
            await windowUtils.scrollToPosition(originalPosition);
            return stitchImage(fullPageSize, parts);
        }

        const [fullPageSize, viewPortSize] = await Promise.all([windowUtils.getFullPageSize(), windowUtils.getViewportSize()]);
        return await createStitchImage(fullPageSize, viewPortSize);
    }
}
